import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Activity, Clock, Database, AlertTriangle, RefreshCw,
  CheckCircle2, XCircle, Loader2, Flame, Zap, Send,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const StatTile = ({ label, value, sub, icon: Icon, accent = "text-mystic-300", testid }) => (
  <Card className="glass" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-mystic-200/50">{label}</p>
          <p className="text-3xl font-semibold text-white mt-2">{value}</p>
          {sub && <p className="text-xs text-mystic-200/60 mt-1.5">{sub}</p>}
        </div>
        <Icon className={`w-8 h-8 ${accent} opacity-70`} />
      </div>
    </CardContent>
  </Card>
);

const cronLabel = (jobId) => {
  switch (jobId) {
    case "daily_cycle_reminder": return "Daily Cycle Reminder";
    case "monthly_summary": return "Monthly Summary";
    case "bbt_reminder": return "BBT Reminder (hourly)";
    case "weekly_fertility_digest": return "Weekly Fertility Digest";
    case "daily_slo_alert": return "Daily SLO Alert (09:00 UTC)";
    default: return jobId;
  }
};

const formatTimeAgo = (iso) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} day(s) ago`;
};

const AdminSystemHealthPage = () => {
  const { token, user } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(24);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async (hours = windowHours) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/system/health?window_hours=${hours}`, { headers, withCredentials: true });
      setHealth(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load system health");
    } finally {
      setLoading(false);
    }
  };

  const sendSloAlertNow = async () => {
    try {
      toast.info("Sending SLO alert digest…");
      const res = await axios.post(`${API}/admin/system/slo-alert/send-now?window_hours=${windowHours}`, null, { headers, withCredentials: true });
      const r = res.data || {};
      toast.success(`SLO alert sent — breaches: ${r.has_breaches ? "YES" : "no"}, recipients: ${r.recipients || 0}`);
      load(windowHours);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send SLO alert");
    }
  };

  useEffect(() => {
    if (token) load(windowHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }
  if (!health) {
    return <div className="text-center py-16 text-mystic-200">Unable to load system health.</div>;
  }

  const dbOk = health.db?.status === "ok";
  const overall = health.overall || {};

  return (
    <div className="space-y-8" data-testid="admin-system-health-page">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between flex-wrap gap-3"
      >
        <div>
          <Link to="/admin" className="text-mystic-300 hover:text-white inline-flex items-center gap-1 text-sm mb-2" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            System <span className="gradient-text">Health</span>
          </h1>
          <p className="text-mystic-200/70 mt-1">DB latency, request percentiles, cron job status</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowHours}
            onChange={(e) => { const h = Number(e.target.value); setWindowHours(h); load(h); }}
            className="bg-mystic-900/60 border border-mystic-500/30 text-mystic-100 text-sm rounded-full px-3 py-2"
            data-testid="window-select"
          >
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
          </select>
          <Button
            onClick={() => load(windowHours)}
            variant="outline"
            size="sm"
            className="border-mystic-500/30 text-mystic-200 hover:text-white rounded-full"
            data-testid="refresh-system-health"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            onClick={sendSloAlertNow}
            size="sm"
            className="bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 text-white rounded-full"
            data-testid="send-slo-alert-now"
          >
            <Send className="w-4 h-4 mr-1" /> Send SLO Alert Now
          </Button>
        </div>
      </motion.div>

      {/* Top tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatTile
          label="DB Latency"
          value={dbOk ? `${health.db.latency_ms} ms` : "ERR"}
          sub={dbOk ? "MongoDB ping round-trip" : health.db?.status}
          icon={Database}
          accent={dbOk ? "text-emerald-300" : "text-rose-400"}
          testid="tile-db-latency"
        />
        <StatTile
          label="Total Requests"
          value={(overall.total_requests || 0).toLocaleString()}
          sub={`window: ${health.window_hours}h`}
          icon={Activity}
          accent="text-mystic-300"
          testid="tile-total-requests"
        />
        <StatTile
          label="Error Rate"
          value={`${overall.error_rate_pct ?? 0}%`}
          sub={`${overall.total_errors || 0} 5xx in window`}
          icon={AlertTriangle}
          accent={(overall.error_rate_pct || 0) > 1 ? "text-rose-400" : "text-emerald-300"}
          testid="tile-error-rate"
        />
        <StatTile
          label="p95 Latency"
          value={`${overall.p95_ms ?? 0} ms`}
          sub={`p50: ${overall.p50_ms ?? 0} ms`}
          icon={Clock}
          accent={(overall.p95_ms || 0) > 1000 ? "text-amber-300" : "text-emerald-300"}
          testid="tile-p95"
        />
        <StatTile
          label="p99 Latency"
          value={`${overall.p99_ms ?? 0} ms`}
          sub="Tail latency"
          icon={Zap}
          accent={(overall.p99_ms || 0) > 3000 ? "text-rose-400" : "text-amber-200"}
          testid="tile-p99"
        />
      </div>

      {/* Slowest endpoints + Hottest endpoints */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="glass" data-testid="slowest-endpoints-card">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-300" />
              Top 10 Slowest Endpoints (by p95)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health.slowest_endpoints.length === 0 ? (
              <p className="text-mystic-200/60 text-sm">No traffic in this window yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-mystic-200/60 text-[11px] uppercase tracking-widest border-b border-mystic-700/40">
                      <th className="py-2 pr-2 font-normal">Route</th>
                      <th className="py-2 pr-2 font-normal text-right">Calls</th>
                      <th className="py-2 pr-2 font-normal text-right">p50</th>
                      <th className="py-2 pr-2 font-normal text-right">p95</th>
                      <th className="py-2 font-normal text-right">p99</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.slowest_endpoints.map((e, idx) => (
                      <tr key={`${e.route}-${idx}`} className="border-b border-mystic-800/40" data-testid={`slowest-row-${idx}`}>
                        <td className="py-2 pr-2 text-mystic-100 font-mono text-[11px]" title={e.route}>{e.route.replace("/api", "")}</td>
                        <td className="py-2 pr-2 text-right text-mystic-100">{e.count.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right text-mystic-200">{e.p50_ms} ms</td>
                        <td className="py-2 pr-2 text-right text-amber-300 font-semibold">{e.p95_ms} ms</td>
                        <td className="py-2 text-right text-rose-300">{e.p99_ms} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass" data-testid="hottest-endpoints-card">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Flame className="w-5 h-5 text-rose-300" />
              Top 10 Hottest Endpoints (by call count)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health.hottest_endpoints.length === 0 ? (
              <p className="text-mystic-200/60 text-sm">No traffic in this window yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-mystic-200/60 text-[11px] uppercase tracking-widest border-b border-mystic-700/40">
                      <th className="py-2 pr-2 font-normal">Route</th>
                      <th className="py-2 pr-2 font-normal text-right">Calls</th>
                      <th className="py-2 pr-2 font-normal text-right">Errors</th>
                      <th className="py-2 font-normal text-right">p95</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.hottest_endpoints.map((e, idx) => (
                      <tr key={`${e.route}-${idx}`} className="border-b border-mystic-800/40" data-testid={`hottest-row-${idx}`}>
                        <td className="py-2 pr-2 text-mystic-100 font-mono text-[11px]" title={e.route}>{e.route.replace("/api", "")}</td>
                        <td className="py-2 pr-2 text-right text-mystic-100 font-semibold">{e.count.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right text-rose-300">{e.errors}</td>
                        <td className="py-2 text-right text-mystic-200">{e.p95_ms} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cron jobs */}
      <Card className="glass" data-testid="cron-jobs-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-mystic-300" />
            Background Jobs (Cron)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {health.cron_jobs.map((j) => {
              const ok = j.status === "ok";
              const err = j.status === "error";
              const never = j.status === "never_run";
              const iconColor = ok ? "text-emerald-300" : err ? "text-rose-400" : "text-mystic-300/50";
              const IconEl = ok ? CheckCircle2 : err ? XCircle : Clock;
              return (
                <div
                  key={j.job_id}
                  data-testid={`cron-${j.job_id}`}
                  className="rounded-xl bg-mystic-900/40 border border-mystic-700/30 p-4 flex items-start gap-3"
                >
                  <IconEl className={`w-5 h-5 mt-0.5 ${iconColor}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{cronLabel(j.job_id)}</p>
                    <p className="text-mystic-200/60 text-xs mt-0.5">
                      {never ? "Has not run yet in this environment" : `Last run: ${formatTimeAgo(j.last_run_at)}`}
                    </p>
                    {j.result && (
                      <p className="text-mystic-200/40 text-[11px] mt-1 font-mono break-all">
                        {JSON.stringify(j.result)}
                      </p>
                    )}
                    {j.error && (
                      <p className="text-rose-300/80 text-[11px] mt-1 break-words">⚠ {j.error}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <p className="text-mystic-200/40 text-xs text-center pt-4">
        Logged in as {user?.email} · Last refreshed {new Date(health.generated_at).toLocaleString()}
      </p>
    </div>
  );
};

export default AdminSystemHealthPage;
